import { BaseService } from "./BaseService";
import { UserProfile } from "@/types/user-profile";
import {
  CreateFrontDeskCustomRoleUserDto,
  CreateStaffUserDto,
  UpdateUserDto,
} from "@/types/user";
import { Team } from "./teamService";

export interface GetStaffFilters {
  hotelId?: string;
  isActive?: boolean;
  teamId?: string;
  teamIds?: string[];
  minTasks?: number;
  email?: string;
  currentPage?: number;
  perPage?: number;
  name?: string;
  userIds?: string[];
  position?: string;
  sortBy?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
}

export interface UserTeam {
  teamId: string;
  staffId: string;
  team: {
    id: string;
    name: string;
  };
}

export interface StaffWithRelations extends UserProfile {
  staff: {
    id: string;
    isActive: boolean;
    tasks: any[];
    position: string;
    userTeams: {
      teamId: string;
      staffId: string;
      team: Team;
    }[];
  } | null;
}

export interface GetStaffResponse {
  users: StaffWithRelations[];
  total: number;
  teamCounts: Record<string, number>;
}

class StaffService extends BaseService {
  private basePath = "/manager/users/staff";

  constructor() {
    super();
  }

  async getStaff(filters?: GetStaffFilters): Promise<GetStaffResponse> {
    if (!filters?.hotelId) return { users: [], total: 0, teamCounts: {} };
    return this.get<GetStaffResponse>(this.basePath, filters);
  }

  async getStaffById(id: string): Promise<StaffWithRelations> {
    return this.get<StaffWithRelations>(`${this.basePath}/${id}`);
  }

  async createStaff(userData: CreateStaffUserDto): Promise<UserProfile> {
    return this.post<UserProfile>(this.basePath, userData);
  }

  async createFrontDesk(
    userData: CreateFrontDeskCustomRoleUserDto,
  ): Promise<UserProfile> {
    return this.post<UserProfile>("/manager/users/frontdesk", userData);
  }

  async updateFrontDeskStatus(id: string, isActive: boolean): Promise<void> {
    return this.patch<void>(`/manager/users/frontdesk/${id}/status`, {
      isActive,
    });
  }

  async updateStaff(id: string, userData: UpdateUserDto): Promise<void> {
    return this.patch<void>(`${this.basePath}/${id}`, userData);
  }

  async deleteStaff(id: string): Promise<void> {
    return this.delete<void>(`${this.basePath}/${id}`);
  }

  async searchStaff(filters?: GetStaffFilters): Promise<StaffSearchResult[]> {
    return this.get<StaffSearchResult[]>("/manager/users/search", filters);
  }

  async sendPasswordResetEmail(id: string): Promise<{ message: string }> {
    return this.post<{ message: string }>(
      `${this.basePath}/${id}/reset-password`,
      {},
    );
  }

  async getStaffPositions(hotelId: string): Promise<{ positions: string[] }> {
    return this.get<{ positions: string[] }>(`${this.basePath}/positions`, {
      hotelId,
    });
  }

  async importStaff(file: File): Promise<any> {
    return this.uploadFile<any>(`${this.basePath}/import`, file);
  }
}

export interface StaffSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  position?: string;
}

export const staffService = new StaffService();
